/**
 * メール連携 (/api/email-integrations) の API hooks.
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

/**
 * メール連携の型.
 */
export type EmailConnection = {
  id: string;
  provider: string;
  emailAddress: string;
  displayName: string | null;
  profilePictureUrl: string | null;
  status: string; // "connected" | "disconnected" | "error"
  lastSyncedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
};

type EmailConnectionListResponse = { items: EmailConnection[] };

type EmailOAuthAuthorizeResponse = {
  authorizeUrl: string;
  state: string;
};

/**
 * Unknown error を RestApiError に正規化する.
 * @param {unknown} e catch した値
 * @return {RestApiError} 正規化済みエラー
 */
const toRestApiError = (e: unknown): RestApiError =>
  e instanceof RestApiError
    ? e
    : new RestApiError(e instanceof Error ? e.message : "Unknown error", 500);

/**
 * 連携中アカウント一覧を取得する hook.
 * @return {object} connections, loading, error, refetch
 */
export function useEmailConnections(): {
  connections: EmailConnection[];
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [connections, setConnections] = useState<EmailConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await restClient.get<EmailConnectionListResponse>(
        "/email-integrations",
      );
      setConnections(res.items);
    } catch (e) {
      setError(toRestApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { connections, loading, error, refetch: fetch };
}

/**
 * アクティブな Gmail 連携の有無を判定するヘルパ hook.
 * 1:1 メール送信機能 (返信 / 提案) のゲートに使う.
 * @return {object} hasActiveConnection, connection, loading, refetch
 */
export function useActiveGmailConnection(): {
  hasActiveConnection: boolean;
  connection: EmailConnection | null;
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const { connections, loading, refetch } = useEmailConnections();
  const connection =
    connections.find(c => c.provider === "gmail" && c.status === "connected") ??
    null;
  return {
    hasActiveConnection: connection !== null,
    connection,
    loading,
    refetch,
  };
}

/** 1:1 送信モーダル (返信 / 提案) の送信元プルダウン用に整形した情報. */
export type ComposeSenderOption = {
  email: string;
  displayName: string;
};

/**
 * 1:1 送信モーダル用の送信元一覧 hook.
 * **配信機能は対象外** - 配信機能は引き続き sender_emails (SES verified) を使う.
 *
 * ログイン中ユーザー本人の Gmail 連携 (status=connected) を整形して返す.
 * 未連携時は空配列, 連携 1 件なら 1 件, 複数連携していれば全件.
 * @return {object} senders, loading, refetch
 */
export function useComposeSenders(): {
  senders: ComposeSenderOption[];
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const { connections, loading, refetch } = useEmailConnections();
  const senders: ComposeSenderOption[] = connections
    .filter(c => c.provider === "gmail" && c.status === "connected")
    .map(c => ({
      email: c.emailAddress,
      displayName: c.displayName ?? c.emailAddress,
    }));
  return { senders, loading, refetch };
}

/**
 * OAuth 認可 URL を取得してプロバイダーにリダイレクトする hook.
 * @return {object} start, loading, error
 */
export function useStartEmailOAuth(): {
  start: (provider: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const start = useCallback(async (provider: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await restClient.get<EmailOAuthAuthorizeResponse>(
        `/email-integrations/${provider}/oauth/authorize`,
      );
      window.location.href = res.authorizeUrl;
    } catch (e) {
      const err = toRestApiError(e);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { start, loading, error };
}

/**
 * OAuth コールバック (code/state → サーバーに POST) を処理する hook.
 * @return {object} processCallback, loading, error
 */
export function useEmailOAuthCallback(): {
  processCallback: (
    provider: string,
    code: string,
    state: string,
  ) => Promise<EmailConnection>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const processCallback = useCallback(
    async (
      provider: string,
      code: string,
      state: string,
    ): Promise<EmailConnection> => {
      setLoading(true);
      setError(null);
      try {
        return await restClient.post<EmailConnection>(
          `/email-integrations/${provider}/oauth/callback`,
          { code, state },
        );
      } catch (e) {
        const err = toRestApiError(e);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { processCallback, loading, error };
}

/**
 * 連携を解除する hook.
 * @return {object} disconnect, loading, error
 */
export function useDisconnectEmail(): {
  disconnect: (connectionId: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const disconnect = useCallback(
    async (connectionId: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await restClient.delete(`/email-integrations/${connectionId}`);
      } catch (e) {
        const err = toRestApiError(e);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { disconnect, loading, error };
}
